// Ein abgeschlossenes Protokoll als Vorlage für die nächste Übergabe.
//
// Der Anlass (Patrick, 25.08.2026): Auf einen Auszug folgt fast immer ein Einzug in
// derselben Wohnung — Räume, Zähler und Schlüssel sind dieselben, oft sogar die Fotos.
// Alles noch einmal einzutippen und neu zu fotografieren war der größte Zeitfresser.
//
// Übernommen wird der Zustand der Wohnung, NICHT der Vorgang: Parteien, Unterschriften,
// PDF, Ablage und Versand gehören zur alten Übergabe und bleiben leer.
import type { Art, Protokoll } from './typen';

/** Auszug ↔ Einzug; „kauf" hat kein Gegenstück und bleibt. */
export function gegenstueckArt(art: Art): Art {
  if (art === 'auszug') return 'einzug';
  if (art === 'einzug') return 'auszug';
  return art;
}

/** Medienpfade liegen unter `${protokollId}/…` — beim Kopieren zeigt alles in den neuen Ordner. */
export function schreibePfadeUm(pfad: string, alteId: string, neueId: string): string {
  return pfad.startsWith(`${alteId}/`) ? `${neueId}/${pfad.slice(alteId.length + 1)}` : pfad;
}

/** Alle Bilddateien eines Protokolls, jede genau einmal — Grundlage fürs Kopieren im Bucket. */
export function medienPfade(p: Protokoll): string[] {
  const alle = [
    ...(p.raeume || []).flatMap((r) => [
      ...(r.fotos || []),
      ...(r.pruefpunkte || []).flatMap((pp) => pp.fotos || []),
    ]),
    ...(p.zaehler || []).map((z) => z.foto).filter((f): f is string => !!f),
  ];
  return [...new Set(alle)];
}

/**
 * Baut aus einem bestehenden Protokoll den Entwurf für die nächste Übergabe.
 * Die neue ID muss der Aufrufer vorgeben — er braucht sie ohnehin, um vorher die
 * Bilder in den neuen Ordner zu kopieren.
 */
export function kopiereProtokoll(
  vorlage: Protokoll,
  opts: { neueId: string; art: Art; erfasstVon: string | null },
): Protokoll {
  const um = (pfad: string) => schreibePfadeUm(pfad, vorlage.id, opts.neueId);
  const jetzt = new Date().toISOString();

  return {
    id: opts.neueId,
    art: opts.art,
    status: 'in_erfassung',
    // Dieselbe Wohnung — deshalb bleiben Objekt und Einheit.
    objekt_id: vorlage.objekt_id,
    einheit_id: vorlage.einheit_id,
    // Personen gehören zum Vorgang, nicht zur Wohnung.
    mieter_id: null,
    eigentuemer_id: null,
    vertrag_id: null,
    vorgaenger_id: vorlage.id,
    durchgefuehrt_am: jetzt,
    erfasst_von: opts.erfasstVon,
    parteien: [],
    raeume: (vorlage.raeume || []).map((r) => ({
      ...r,
      fotos: (r.fotos || []).map(um),
      pruefpunkte: (r.pruefpunkte || []).map((pp) => ({ ...pp, fotos: (pp.fotos || []).map(um) })),
    })),
    zaehler: (vorlage.zaehler || []).map((z) => ({
      ...z,
      foto: z.foto ? um(z.foto) : null,
      // Die Ablesung gehört zum alten Termin — sonst hinge die neue Übergabe an einem
      // fremden Zählerstand-Datensatz.
      ablesung_id: null,
    })),
    schluessel: (vorlage.schluessel || []).map((s) => ({ ...s })),
    bemerkungen: vorlage.bemerkungen,
    renoviert_am: vorlage.renoviert_am,
    nebenkosten_bezahlt: null,
    nebenkosten_notiz: null,
    unterschriften: [],
    pdf_path: null,
    pdf_hash: null,
    drive_file_id: null,
    mail_versendet_am: null,
    created_at: jetzt,
    updated_at: jetzt,
  };
}
